import { pathOr } from 'ramda';
import { parse } from 'querystring';

import { isJourneyTimeRestricted } from 'utils/isJourneyTimeRestricted';
import Currency from 'utils/Currency';
import { sum } from 'utils/calculator';
import { MULTI_JOURNEY_KEY } from 'constants/booking';

export const buildSegmentItems = (segment) =>
  segment.products.map(({ passengerId, tariffCode }) => ({
    passengerId,
    tariffCode
  }));

export const buildSegments = ({ outbound, inbound }, isReturnJourney) => {
  const segments = outbound.legs.map((leg) => ({
    direction: 'outbound',
    origin: leg.origin.uic,
    destination: leg.destination.uic,
    startValidityDate: leg.timing.departureDate,
    serviceName: leg.serviceName,
    serviceIdentifier: leg.serviceIdentifier,
    items: buildSegmentItems(leg)
  }));

  if (isReturnJourney) {
    segments.push(
      ...inbound.legs.map((leg) => ({
        direction: 'inbound',
        origin: leg.origin.uic,
        destination: leg.destination.uic,
        startValidityDate: leg.timing.departureDate,
        serviceName: leg.serviceName,
        serviceIdentifier: leg.serviceIdentifier,
        items: buildSegmentItems(leg)
      }))
    );
  }

  return segments;
};

export const getTotalPrice = ({ outbound, inbound, fees = [] }) => {
  const reducer = (totalPrice, out) => {
    const passengerPrice = Currency.sum(out.products.map((item) => item.price));

    return totalPrice + passengerPrice;
  };

  const outboundTotalPrice = (outbound.legs && outbound.legs.reduce(reducer, 0)) || 0;
  const inboundTotalPrice = (inbound.legs && inbound.legs.reduce(reducer, 0)) || 0;

  return {
    totalPrice: Currency.sum([outboundTotalPrice, inboundTotalPrice, ...fees]),
    outboundTotalPrice,
    inboundTotalPrice
  };
};

export const hasProductFamilyMismatch = (requestedProductFamilies = [], returnedProductFamilies = []) => {
  if (returnedProductFamilies.length === 0 || requestedProductFamilies.length === 0) {
    return false;
  }
  if (returnedProductFamilies.length === 1) {
    return !requestedProductFamilies.includes(returnedProductFamilies[0].code);
  }

  return true;
};

export const getSearchParams = (search) => {
  const {
    adults,
    adults12To15,
    adults16Plus,
    children,
    children4Only,
    children5To11,
    infants,
    youths,
    seniors,
    adultsWheelchair,
    childrenWheelchair,
    guideDogs,
    wheelchairCompanions,
    nonWheelchairCompanions,
    origin,
    destination,
    outboundDate,
    inboundDate,
    returningOrigin,
    returningDestination,
    contractCode,
    productFamilies,
    currency
  } = parse(search.substr(1));

  return {
    adults: Number(adults),
    adults12To15: Number(adults12To15),
    adults16Plus: Number(adults16Plus),
    adultsWheelchair: Number(adultsWheelchair),
    children: Number(children),
    children4Only: Number(children4Only),
    children5To11: Number(children5To11),
    childrenWheelchair: Number(childrenWheelchair),
    youths: Number(youths),
    seniors: Number(seniors),
    contractCode,
    currency,
    destination,
    guideDogs: Number(guideDogs),
    inboundDate,
    infants: Number(infants),
    nonWheelchairCompanions: Number(nonWheelchairCompanions),
    origin,
    outboundDate,
    productFamilies: productFamilies ? [productFamilies] : [],
    returningDestination,
    returningOrigin,
    wheelchairCompanions: Number(wheelchairCompanions),
    multipleFlexibility: true
  };
};

export const buildOutboundJourneys = ({ data, outboundDate, inboundDate, selectedInbound }) =>
  pathOr([], ['journeySearch', 'outbound', 'journeys'], data).map((j) => ({
    ...j,
    isTimeRestricted: isJourneyTimeRestricted({
      outboundDate,
      outboundArrives: j.timing.arrives,
      inboundDate,
      inboundDeparts: selectedInbound && selectedInbound.legs[0].timing.departs
    })
  }));

export const buildInboundJourneys = ({ data, outboundDate, inboundDate, selectedOutbound }) =>
  pathOr([], ['journeySearch', 'inbound', 'journeys'], data).map((j) => ({
    ...j,
    isTimeRestricted: isJourneyTimeRestricted({
      outboundDate,
      outboundArrives: selectedOutbound && selectedOutbound.legs[selectedOutbound.legs.length - 1].timing.arrives,
      inboundDate,
      inboundDeparts: j.timing.departs
    })
  }));

export const buildLegFromFare = (selectedFare, i = 0) =>
  selectedFare
    ? {
        bookingItems: selectedFare.bookingItems.map(({ tariffCode, ...others }) => ({
          tariffDetails: { tariffCode },
          productFamily: selectedFare.productFamily,
          ...others
        })),
        legs: selectedFare.legs[i],
        origin: selectedFare.legs[i].origin,
        destination: selectedFare.legs[i].destination,
        date: selectedFare.legs[i].timing.departureDate,
        arrivalTime: selectedFare.legs[i].timing.arrives,
        departureTime: selectedFare.legs[i].timing.departs,
        classOfService: selectedFare.classOfService,
        productFamily: selectedFare.productFamily
      }
    : null;

export const buildLegsFromFare = (selectedFare) => {
  const fares = [];

  if (selectedFare) {
    for (let i = 0; i < selectedFare.legs.length; i += 1) {
      fares.push(buildLegFromFare(selectedFare, i));
    }
  }

  return fares;
};

export const buildInboundInfo = ({
  date,
  returningOriginStationName,
  returningDestinationStationName,
  destinationStationName,
  originStationName,
  originUIC,
  destinationUIC,
  returningOriginUIC,
  returningDestinationUIC
}) => {
  if (!date) {
    return null;
  }

  const isMultiCityJourney = Boolean(returningOriginStationName && returningDestinationStationName);
  const origin = isMultiCityJourney ? returningOriginStationName : destinationStationName;
  const destination = isMultiCityJourney ? returningDestinationStationName : originStationName;

  const inboundOriginUIC = isMultiCityJourney ? returningOriginUIC : destinationUIC;
  const inboundDestinationUIC = isMultiCityJourney ? returningDestinationUIC : originUIC;

  return {
    date,
    origin,
    destination,
    originUIC: inboundOriginUIC,
    destinationUIC: inboundDestinationUIC
  };
};

export const calcSegmentPointsTotal = (segment) =>
  segment?.legs?.reduce((acc, leg) => {
    const points = leg.products.map((product) => product.loyaltyPoints);

    return sum([acc, ...points]);
  }, 0);

export const getTotalAddonPrice = ({ outbound, inbound }) => {
  const reducer = (prices, acc) => {
    const addonPrice = acc?.addOnLeg ? Currency.sum(acc?.addOnLeg?.products?.map((item) => item?.price)) : 0;

    return prices + addonPrice;
  };

  const outboundTotalAddOnPrice = outbound?.reduce(reducer, 0) || 0;
  const inboundTotalAddOnPrice = inbound?.reduce(reducer, 0) || 0;

  return {
    outboundTotalAddOnPrice,
    inboundTotalAddOnPrice
  };
};

export const isBookingAlreadyMultiJourney = (booking) => booking.additionalDetails?.some((ad) => ad.key === MULTI_JOURNEY_KEY);

export const buildMultiJourneyAdditionalDetails = (oldBooking, newBooking) => {
  const additionalDetails = [];

  const shouldAddMultiJourney = !isBookingAlreadyMultiJourney(oldBooking);
  let journeyIdx = 1;

  if (shouldAddMultiJourney) {
    // add multi journey flag
    additionalDetails.push({
      key: MULTI_JOURNEY_KEY,
      value: 'true'
    });

    // add segments from first journey
    oldBooking.outbound.concat(oldBooking.inbound).forEach((leg) => {
      additionalDetails.push({
        key: leg.id,
        value: `journey_${journeyIdx}`
      });
    });

    journeyIdx++;
  } else {
    // get next index from additional details
    newBooking.additionalDetails
      .filter((ad) => ad.key.includes('segment_') && ad.value.includes('journey_'))
      .forEach((ad) => {
        const idx = Number(ad.value.split('_')[1]);

        if (idx > journeyIdx) {
          journeyIdx = idx;
        }
      });
    journeyIdx++;
  }

  // add new segments
  newBooking.outbound
    .concat(newBooking.inbound)
    .filter((leg) => !newBooking.additionalDetails.filter((ad) => ad.value.includes('journey_')).find((ad) => ad.key === leg.id))
    .filter((leg) => !additionalDetails.filter((ad) => ad.value.includes('journey_')).find((ad) => ad.key === leg.id))
    .forEach((leg) => {
      additionalDetails.push({
        key: leg.id,
        value: `journey_${journeyIdx}`
      });
    });

  return additionalDetails;
};
